import React, { useState, useEffect } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
  Container,
  Row,
  Col,
  Modal,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";
import { Tabs, Tab, Card, CardHeader, CardBody } from "react-bootstrap";
import { Dropdown, ButtonGroup } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { CircleLoader } from "react-spinners";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import FormInput from "../../../components/FormInput";

import { createClient, updateClient, getClient } from "../../../api/clients";
import { successNotify, errorNotify } from "../../../utils/toast";

// validations
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";

import { ClientDataType } from "../../../data/client";

import { useSnackbar } from "../../../utils/snackbar";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../../components/LanguageSelector";

const NewClientPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { id } = useParams();

  // state variables

  const [loading, setLoading] = useState<boolean>(false);
  const [filterText, setFilterText] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);

  const [selectedGenderValue, setSelectedGenderValue] = useState<string>("1");
  const [selectedCountryValue, setSelectedCountryValue] = useState<string>("1");
  const [selectedCurrencyValue, setSelectedCurrencyValue] =
    useState<string>("1");
  const [bankType, setBankType] = useState<string>("1");

  const [isVisible, setIsVisible] = useState<boolean>(false);

  const [selectAll, setSelectAll] = useState(false);
  const [clientData, setClientData] = useState<ClientDataType | null>();

  const defaultValues: any = {
    name_invoice_recipient: clientData?.name_invoice_recipient,
    email: clientData?.email,
    company_name: clientData?.company_name,
  };

  const resolver = yupResolver(
    yup.object().shape({
      name_invoice_recipient: yup.string().required("Please Enter Full Name"),
      email: yup
        .string()
        .required("Please Enter E-mail.")
        .email("This value should be a valid email."),
    }),
  );

  const methods = useForm({ defaultValues, resolver });
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    const fetchClient = async () => {
      if (!id) return;
      const payload = {
        clientId: id,
      };

      try {
        const clientRes = await getClient(payload);
        if (clientRes.success == true) {
          if (clientRes) {
            const clientData = clientRes?.data;

            setSelectedGenderValue(clientData?.gender_type);
            setBankType(clientData?.bank_type);
            setSelectedCountryValue(clientData?.country_type);
            setSelectedCurrencyValue(clientData?.currency_type);

            reset({
              name_invoice_recipient: clientData?.name_invoice_recipient,
              email: clientData?.email,
              account_holder: clientData?.account_holder,
              apt_suite: clientData?.apt_suite,
              bank: clientData?.bank,
              bank_type: clientData?.bank_type,
              bic: clientData?.bic,
              city: clientData?.city,
              company_name: clientData?.company_name,
              country_type: clientData?.country_type,
              eori_number: clientData?.eori_number,
              fax: clientData?.fax,
              gender_type: clientData?.gender_type,
              iban: clientData?.iban,
              mobile_number: clientData?.mobile_number,
              phone_number: clientData?.phone_number,
              postal_code: clientData?.postal_code,
              state: clientData?.state,
              street: clientData?.street,
              vat_id: clientData?.vat_id,
              website: clientData?.website,
              paypal_account: clientData?.paypal_account,
            });
          }
        } else {
          console.log(clientRes?.error);
        }
      } catch (error: any) {
        console.error("Error fetching client:", error);
      }
    };

    fetchClient();
  }, []);

  const onSubmitForm = async (values: any) => {
    setLoading(true);

    const payload = {
      ...values,
      gender_type: selectedGenderValue,
      country_type: selectedCountryValue,
      currency_type: selectedCurrencyValue,
      bank_type: bankType,
    };

    try {
      let response: any;

      if (id) {
        const updatePayload = { ...payload, id: id };
        response = await updateClient(updatePayload);
      } else {
        response = await createClient(payload);
      }

      if (response.success == false) {
        showSnackbar(response.message, "error");
      } else {
        showSnackbar(response.message, "info");
      }

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  //component functions
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGenderValue(event.target.value);
  };

  const handleSelectCountryChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedCountryValue(event.target.value);
  };
  const handleSelectCurrencyChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedCurrencyValue(event.target.value);
  };

  const handleSelectBankType = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setBankType(event.target.value);
  };

  const handleClose = (): void => setShowModal(false);
  const handleShow = (): void => setShowModal(true);

  const handleGoToSubscriptionPage = () => {
    navigate("/subscription");
  };

  return (
    <div className="right-panel">
      <div className="sticky-top bg-white shadow-sm w-100 p-3 d-flex justify-content-between align-items-center z-index-100">
        <span className="mb-0 default-text" style={{ fontWeight: "500" }}>
          {t("Clients")}
        </span>
        <div className="d-flex gap-2">
          <LanguageSwitcher />
          <Button variant="danger" onClick={handleGoToSubscriptionPage}>
            {t("Unlock Pro")}
          </Button>
          <div>
            <Button
              variant="primary"
              onClick={handleSubmit(onSubmitForm)}
              disabled={loading}
            >
              {id ? t("Update") : t("Save")}
              {loading && (
                <Spinner animation="border" size="sm" className="ms-2" />
              )}
            </Button>
          </div>
        </div>
      </div>
      <Form>
        <div className="d-flex justify-content-between align-items-center  px-4 py-3">
          <div id="breadcrumb" className="d-flex gap-2 my-3">
            <i className="bx bx-home font-size-22"></i>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0 h-5 w-5"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <span>
              <a style={{ fontWeight: "500" }}> {t("Clients")}</a>
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0 h-5 w-5"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <span>
              <a style={{ fontWeight: "500" }}>
                {id ? t("Edit Client") : t("New Client")}{" "}
              </a>
            </span>
            <div></div>
          </div>
        </div>
        <div className="py-3 px-4">
          <Row>
            <Col lg={6}>
              <Card>
                <CardHeader className="card-header">
                  {t("User Details")}
                </CardHeader>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Company Name")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="company_name"
                        labelClassName="form-label"
                        placeholder={t("Company Name")}
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Client Name")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="name_invoice_recipient"
                        labelClassName="form-label"
                        placeholder={t("Client Name")}
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Gender")}</span>
                    </Col>
                    <Col md="8">
                      <Form.Group controlId="exampleSelect">
                        <Form.Select
                          value={selectedGenderValue}
                          onChange={handleSelectChange}
                          aria-label="Select gender"
                          name="gender"
                        >
                          <option value="1">{t("Mr")}</option>
                          <option value="2">{t("Mrs")}</option>
                          <option value="3">{t("Miss")}</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Email")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="email"
                        labelClassName="form-label"
                        placeholder={t("example@mail.com")}
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Phone")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="phone_number"
                        labelClassName="form-label"
                        placeholder="+49 12 34 5678"
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Mobile")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="mobile_number"
                        labelClassName="form-label"
                        placeholder="12 34 5678"
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Fax")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="fax"
                        labelClassName="form-label"
                        placeholder={t("Fax Number")}
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Website")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="website"
                        labelClassName="form-label"
                        placeholder={t("example.com")}
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card>
                <CardHeader className="card-header">
                  {t("Bank Info")}
                </CardHeader>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Payment Type")}</span>
                    </Col>
                    <Col md="8">
                      <Form.Group controlId="exampleSelect">
                        <Form.Select
                          value={bankType}
                          onChange={handleSelectBankType}
                          aria-label="Select example"
                        >
                          <option value="1">{t("Bank(SEPA)")}</option>
                          <option value="2">{t("Bank(Standard)")}</option>
                          <option value="3">{t("Paypal")}</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  {bankType == "1" && (
                    <>
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">
                            {t("Account Holder")}
                          </span>
                        </Col>
                        <Col md="8">
                          <FormInput
                            type="text"
                            name="account_holder"
                            labelClassName="form-label"
                            placeholder=""
                            className="form-control"
                            register={register}
                            errors={errors}
                            control={control}
                          />
                        </Col>
                      </Row>
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">{t("IBAN")}</span>
                        </Col>
                        <Col md="8">
                          <FormInput
                            type="text"
                            name="iban"
                            labelClassName="form-label"
                            placeholder=""
                            className="form-control"
                            register={register}
                            errors={errors}
                            control={control}
                          />
                        </Col>
                      </Row>
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">{t("Bank")}</span>
                        </Col>
                        <Col md="8">
                          <FormInput
                            type="text"
                            name="bank"
                            labelClassName="form-label"
                            placeholder=""
                            className="form-control"
                            register={register}
                            errors={errors}
                            control={control}
                          />
                        </Col>
                      </Row>
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">{t("BIC")}</span>
                        </Col>
                        <Col md="8">
                          <FormInput
                            type="text"
                            name="bic"
                            labelClassName="form-label"
                            placeholder=""
                            className="form-control"
                            register={register}
                            errors={errors}
                            control={control}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                  {bankType == "2" && (
                    <>
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">
                            {t("Account Holder")}
                          </span>
                        </Col>
                        <Col md="8">
                          <FormInput
                            type="text"
                            name="account_holder"
                            labelClassName="form-label"
                            placeholder=""
                            className="form-control"
                            register={register}
                            errors={errors}
                            control={control}
                          />
                        </Col>
                      </Row>
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">{t("IBAN")}</span>
                        </Col>
                        <Col md="8">
                          <FormInput
                            type="text"
                            name="iban"
                            labelClassName="form-label"
                            placeholder=""
                            className="form-control"
                            register={register}
                            errors={errors}
                            control={control}
                          />
                        </Col>
                      </Row>
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">{t("Bank")}</span>
                        </Col>
                        <Col md="8">
                          <FormInput
                            type="text"
                            name="bank"
                            labelClassName="form-label"
                            placeholder=""
                            className="form-control"
                            register={register}
                            errors={errors}
                            control={control}
                          />
                        </Col>
                      </Row>
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">{t("BIC")}</span>
                        </Col>
                        <Col md="8">
                          <FormInput
                            type="text"
                            name="bic"
                            labelClassName="form-label"
                            placeholder=""
                            className="form-control"
                            register={register}
                            errors={errors}
                            control={control}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                  {bankType == "3" && (
                    <>
                      <Row className="g-2 mb-3">
                        <Col md={4} className="vertical-center">
                          <span className="default-text">
                            {t("Paypal Account")}
                          </span>
                        </Col>
                        <Col md="8">
                          <FormInput
                            type="text"
                            name="paypal_account"
                            labelClassName="form-label"
                            placeholder=""
                            className="form-control"
                            register={register}
                            errors={errors}
                            control={control}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <CardHeader className="card-header">{t("Address")}</CardHeader>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">
                        {t("Billing Street")}
                      </span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="street"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Apt/Suite")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="apt_suite"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>

                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Postal Code")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="postal_code"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>

                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("City")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="city"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">
                        {t("State/Province")}
                      </span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="state"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>

                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Country")}</span>
                    </Col>
                    <Col md="8">
                      <Form.Group controlId="country">
                        <Form.Select
                          value={selectedCountryValue}
                          onChange={handleSelectCountryChange}
                          aria-label="Select example"
                        >
                          <option value="1">{t("Germany")}</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <Card>
                <CardHeader className="card-header">
                  {t("Additional Info")}
                </CardHeader>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("VAT ID")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="vat_id"
                        labelClassName="form-label"
                        placeholder="DE12345"
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("EORI Number")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="eori_number"
                        labelClassName="form-label"
                        placeholder="DE12345"
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Currency")}</span>
                    </Col>
                    <Col md="8">
                      <Form.Group controlId="">
                        <Form.Select
                          value={selectedCurrencyValue}
                          onChange={handleSelectCurrencyChange}
                          aria-label=""
                        >
                          <option value="1">Euro (EUR) €</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Form>
    </div>
  );
};

export default NewClientPage;
